import { siteSettings } from "../static";

function requireBunnyConfig() {
  const {
    bunnyStorageZone,
    bunnyAccessKey,
    bunnyStorageHost = "storage.bunnycdn.com",
    mediaBaseUrl,
  } = siteSettings;

  if (!bunnyStorageZone || !bunnyAccessKey || !mediaBaseUrl) {
    throw new Error(
      "Bunny is not configured. Set mediaBaseUrl, bunnyStorageZone, bunnyAccessKey in siteSettings."
    );
  }

  return {
    zone: bunnyStorageZone,
    accessKey: bunnyAccessKey,
    host: bunnyStorageHost.replace(/^https?:\/\//, "").replace(/\/$/, ""),
    cdnBase: mediaBaseUrl.replace(/\/$/, ""),
  };
}

function sanitizeFileName(name) {
  return String(name || `upload-${Date.now()}`)
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .replace(/[^\w.\-()]+/g, "_");
}

function buildObjectPath({ folder, fileName, file }) {
  const baseName = sanitizeFileName(
    fileName || (file && file.name) || `file-${Date.now()}`
  );
  const unique = `${Date.now()}-${Math.floor(Math.random() * 1e9)}-${baseName}`;
  const prefix = folder ? String(folder).replace(/^\/|\/$/g, "") + "/" : "";
  return `${prefix}${unique}`;
}

/**
 * Upload a File/Blob to Bunny Storage. Returns the public CDN URL.
 */
export function uploadMedia(file, options = {}) {
  const { zone, accessKey, host, cdnBase } = requireBunnyConfig();
  if (!file) return Promise.reject(new Error("No file to upload"));

  const objectPath = buildObjectPath({
    folder: options.folder,
    fileName: options.fileName,
    file,
  });

  const putUrl = `https://${host}/${zone}/${objectPath
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", putUrl);
    xhr.setRequestHeader("AccessKey", accessKey);
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream"
    );

    xhr.upload.onprogress = (event) => {
      if (!options.onProgress || !event.lengthComputable) return;
      options.onProgress((event.loaded / event.total) * 100);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(`${cdnBase}/${objectPath}`);
      } else {
        reject(
          new Error(`Bunny upload failed (${xhr.status}): ${xhr.responseText}`)
        );
      }
    };
    xhr.onerror = () => reject(new Error("Bunny upload network error"));
    xhr.send(file);
  });
}

/**
 * Drop-in replacement for axios.post(...cloudinary..., formData, config).
 * Returns { data: { secure_url } } so existing callers keep working.
 */
export async function uploadViaBunny(formDataOrFile, config = {}) {
  const file =
    formDataOrFile instanceof FormData
      ? formDataOrFile.get("file")
      : formDataOrFile;

  if (!file) {
    throw new Error("uploadViaBunny: missing file");
  }

  const publicId =
    formDataOrFile instanceof FormData
      ? formDataOrFile.get("public_id")
      : null;

  const url = await uploadMedia(file, {
    folder: config.folder,
    fileName: publicId || config.fileName || (file.name || undefined),
    onProgress: (pct) => {
      if (typeof config.onUploadProgress === "function") {
        config.onUploadProgress({ loaded: pct, total: 100 });
      }
      if (typeof config.onProgress === "function") {
        config.onProgress(pct);
      }
    },
  });

  return { data: { secure_url: url } };
}
